import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Result, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useResetPassword } from '@presentation/hooks/useAuth';
import { ValidationError } from '@core/errors/AppError';
import { setAntdFormErrors } from '@shared/utils/formErrors';
import { paths } from '@app/routes/paths';

interface ResetPasswordFormValues {
  password: string;
  passwordConfirmation: string;
}

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetPassword = useResetPassword();
  const [form] = Form.useForm<ResetPasswordFormValues>();

  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';

  const handleFinish = (values: ResetPasswordFormValues) => {
    resetPassword.mutate(
      { token, email, ...values },
      {
        onSuccess: () => navigate(paths.login, { replace: true }),
        onError: (error) => {
          if (error instanceof ValidationError) {
            setAntdFormErrors(form, error.errors);
          }
        },
      },
    );
  };

  const generalError =
    resetPassword.error && !(resetPassword.error instanceof ValidationError)
      ? resetPassword.error.message
      : null;

  if (!token || !email) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-sm">
          <Result
            status="error"
            title="Invalid link"
            subTitle="This password reset link is missing required information."
            extra={<Link to={paths.forgotPassword}>Request a new link</Link>}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <Typography.Title level={4} className="!mb-1">
          Reset password
        </Typography.Title>
        <Typography.Text type="secondary">Choose a new password for {email}.</Typography.Text>

        <Form form={form} layout="vertical" className="mt-6" onFinish={handleFinish}>
          <Form.Item
            name="password"
            label="New password"
            rules={[{ required: true, message: 'Password is required' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" size="large" />
          </Form.Item>

          <Form.Item
            name="passwordConfirmation"
            label="Confirm new password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" size="large" />
          </Form.Item>

          {generalError && <Alert type="error" showIcon title={generalError} className="mb-4" />}

          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={resetPassword.isPending}
          >
            Reset password
          </Button>
        </Form>
      </Card>
    </div>
  );
};
